import { PropsWithChildren, useEffect } from "react";
import { ShareStatus } from "@authory-packages/core";
import { V3Loader } from "../../components/Loader";
import { UNAVAILABLE_RESOURCE_TYPE } from "../../types/unavailable_resource_type";
import { usePublicProfileAccessKeyContext } from "../../context/publicProfileAccessKeyContext";
import { getProfileClient } from "../../utils/getAuthor";
import { useThemeOverride } from "../../theme";
import { NoIndexHead } from "../../components/NoIndexHead";
import { isGatedShareStatus } from "../../utils/isGatedShareStatus";
import dynamic from "next/dynamic";

const GenericUnavailable = dynamic(() => import("../../pagelayouts/generic-error-page").then((m) => m.GenericUnavailable));
const V3ModalPublicPasswordContent = dynamic(() =>
    import("../../components/V3ModalPublicPasswordContent/V3ModalPublicPasswordContent").then((m) => m.V3ModalPublicPasswordContent)
);

interface ProfileAccessGateProps {
    profileShareStatus: ShareStatus;
    overrideBackgroundcolor?: string;
}

export const ProfileAccessGate = ({ profileShareStatus, children, overrideBackgroundcolor }: PropsWithChildren<ProfileAccessGateProps>) => {
    const { setAccessKey } = usePublicProfileAccessKeyContext();
    const { setThemeOverride } = useThemeOverride();

    // Get client profile
    const { author } = getProfileClient(profileShareStatus);

    useEffect(() => {
        if (author.isSuccess) {
            setThemeOverride({
                customization: {
                    profileFontSet: author.data.profileFontSet,
                    backgroundColor: overrideBackgroundcolor ? overrideBackgroundcolor : author.data.backgroundColor || "",
                    accentcolour: author.data.accentcolour || "",
                    profileCollectionLayout: author.data.profileCollectionLayout,
                    defaultView: author.data.defaultView,
                    profileStyle: author.data.profileStyle
                }
            });
        }
    }, [author.isSuccess]);

    // A gated profile is not server rendered, so the robots directive has to come from the gate itself
    const noIndex = isGatedShareStatus(profileShareStatus, ShareStatus.Published) ? <NoIndexHead /> : null;

    if (!author)
        return (
            <>
                {noIndex}
                {children}
            </>
        );

    const { isSuccess, isFetched, isFetching } = author;

    if (profileShareStatus === ShareStatus.PasswordProtected && !isSuccess) {
        return (
            <>
                {noIndex}
                <V3ModalPublicPasswordContent
                    onSubmit={(data) => {
                        setAccessKey(data.password);
                    }}
                    isFetching={isFetching}
                />
            </>
        );
    }

    if (profileShareStatus === ShareStatus.SecureLink && !isSuccess) {
        return (
            <>
                {noIndex}
                {isFetched ? <GenericUnavailable target={UNAVAILABLE_RESOURCE_TYPE.PROFILE} /> : <V3Loader fullScreen />}
            </>
        );
    }

    return (
        <>
            {noIndex}
            {children}
        </>
    );
};
